import { useEffect } from 'react';
import { generatePath, matchPath, Route, Routes, useLocation } from 'react-router-dom';
import { ReactFlowProvider } from 'reactflow';

import styled from 'styled-components';

import { UnsplashProvider } from '@components/add-image/unsplash-api';
import { AiInputAssistProvider } from '@components/ai-input-assist/ai-input-assist-api';
import { ConfettiProvider } from '@components/confetti/provider';
import { Modal } from '@components/modal';
import { Overlay } from '@components/overlay';
import { RouteErrorBoundary } from '@components/route-error-boundary';
import { Sidebar } from '@components/sidebar';
import { ThemeProvider } from '@components/theme-provider';
import { showSuccess, Toaster } from '@components/toaster';
import { useIntercom } from '@hooks/use-intercom';
import { useKeyboardSecretToggle } from '@hooks/use-keyboard-secret-toggle';
import { storage } from '@utils/browser';

import { useSendChat } from 'api/ai-content-assistant/hooks';
import { useCurrentUser } from 'api/authentication/hooks';
import { useFavoritesOrdered } from 'api/favorites/hooks';
import { ExportConvertersProvider } from 'api/files/hooks';
import { loadUnsplashSearch, triggerUnsplashDownload } from 'api/photos/requests';
import { useEditMyDetails } from 'api/users/hooks';
import { useCachedWorkspaces } from 'api/workspaces/hooks';
import { CompanyOnboarding } from 'components/company-onboarding';
import { useForcedExpiredModal } from 'components/trial-expired-modal';
import { CompanyFormatPresetsProvider } from 'contexts/format-presets-provider';
import { PermissionsProvider, usePermissions } from 'contexts/permissions-context';
import { getRoutePaths, RouteMapper } from 'ui/helpers';

import { IntegrationsProvider } from './integrations-provider';
import { useCurrentWorkspace } from './main/helpers';
import { MainNavbar } from './main/navbar/navbar';
import { mainRoutes } from './main/routes';
import { useInitializingModal } from './modals/initializing-company';
import { useIntroductionModal } from './modals/introduction-modal';
import { SettingsNavbar } from './settings/navbar/navbar';
import { settingsPrefix, settingsRoutes } from './settings/routes';

import { CommandPaletteProvider } from '../../components/command-palette';
import { useDocumentTitle } from '../../hooks/use-document-title';

export const routePaths = getRoutePaths([
  { routes: mainRoutes },
  { routes: settingsRoutes, prefix: settingsPrefix },
]);

export const routes = [...mainRoutes, ...settingsRoutes];

const titledRoutes = [
  ...mainRoutes.map((r) => ({ ...r, path: `/${r.path}` })),
  ...settingsRoutes.map((r) => ({ ...r, path: `${settingsPrefix}${r.path}` })),
];

const usePageTitle = () => {
  const { title } = useCurrentWorkspace();
  useDocumentTitle({ routes: titledRoutes, decoration: title });
};

export const AuthenticatedNavigation = () => {
  const currentUser = useCurrentUser();
  usePageTitle();
  useIntroductionModal();
  useInitializingModal();
  useForcedExpiredModal();
  useFavoritesOrdered();

  const sendChat = useSendChat();
  const { setUserData } = useIntercom();
  useKeyboardSecretToggle({
    key: 'has-keyboard-shortcuts-enabled',
    shortcut: 'fiftylocal',
    onSuccess: (value) => showSuccess(`Keyboard shortcuts ${value ? 'disabled' : 'enabled'}`),
  });

  useEffect(() => {
    if (!currentUser) return;

    setUserData({
      id: `journeys-${currentUser.id}`,
      email: currentUser.email,
      name: currentUser.name,
      role: currentUser.role,
      picture: currentUser.picture,
      companyId: currentUser.company.id,
      companyName: currentUser.company.name,
    });
  }, [currentUser, setUserData]);

  const dateToClose = currentUser?.plan?.dateToClose ?? '';
  const isExpired = dateToClose && new Date(dateToClose) < new Date();

  return (
    <Container>
      <ThemeProvider app="journeys" theme={currentUser?.theme} updateTheme={useEditMyDetails}>
        <PermissionsProvider>
          <CommandPaletteProvider isActive={!isExpired}>
            <AiInputAssistProvider sendChatMessage={sendChat.mutateAsync}>
              <UnsplashProvider
                loadUnsplashSearch={loadUnsplashSearch}
                triggerUnsplashDownload={triggerUnsplashDownload}
              >
                <IntegrationsProvider>
                  <CompanyFormatPresetsProvider>
                    <ExportConvertersProvider>
                      <ReactFlowProvider>
                        <Routes>
                          <Route
                            path="*"
                            element={<MainRouteConfig />}
                            errorElement={<RouteErrorBoundary />}
                          />
                          <Route
                            path={`${settingsPrefix}/*`}
                            element={<SettingsRouteConfig />}
                            errorElement={<RouteErrorBoundary />}
                          />
                        </Routes>
                        <Sidebar.Provider />
                        <Modal.Provider />
                        <Overlay.Provider />
                        <ConfettiProvider />
                        <Toaster />
                        <CompanyOnboarding />
                      </ReactFlowProvider>
                    </ExportConvertersProvider>
                  </CompanyFormatPresetsProvider>
                </IntegrationsProvider>
              </UnsplashProvider>
            </AiInputAssistProvider>
          </CommandPaletteProvider>
        </PermissionsProvider>
      </ThemeProvider>
    </Container>
  );
};

const Container = styled.div({
  display: 'flex',
  height: '100vh',
});

const MainRouteConfig = () => {
  const currentUser = useCurrentUser();
  const { canViewTravellerProfiles } = usePermissions();
  const cachedWorkspaces = useCachedWorkspaces();
  const currentWorkspace = storage.get('current-workspace-' + currentUser?.company?.shortId);
  const homeWorkspaceId = currentUser?.company?.onboarding?.status?.config?.homeWorkspaceId;
  const firstWorkspaceId = cachedWorkspaces?.[0]?.shortId;
  const fallbackWorkspace = currentWorkspace || homeWorkspaceId || firstWorkspaceId;
  const defaultRoute = currentWorkspace
    ? generatePath(routePaths.workspaceJourneyList, { workspaceId: currentWorkspace })
    : canViewTravellerProfiles
    ? generatePath(routePaths.travellersList, { '*': 'list' })
    : fallbackWorkspace
    ? generatePath(routePaths.workspaceJourneyList, { workspaceId: fallbackWorkspace })
    : generatePath(routePaths.travellersList, { '*': 'list' });
  const { hideNavbar } = useShowNavbar();

  return (
    <>
      {!hideNavbar && <MainNavbar />}
      <RouteMapper routes={mainRoutes} defaultRoute={defaultRoute} />
    </>
  );
};

const SettingsRouteConfig = () => {
  const { canManageCompany } = usePermissions();
  const defaultSettingsRoute = canManageCompany
    ? routePaths.settingsGeneral
    : routePaths.settingsProfile;

  return (
    <>
      <SettingsNavbar />
      <RouteMapper routes={settingsRoutes} defaultRoute={defaultSettingsRoute} />
    </>
  );
};

const useShowNavbar = () => {
  const location = useLocation();
  const route = mainRoutes.find((path) => matchPath(path, location.pathname)) ?? {};

  return { hideNavbar: 'hideNavbar' in route };
};
